import { useEffect, useRef, useCallback, useState } from "react";

interface UseIdleOptions {
  /** Seconds of inactivity before firing onIdle. */
  timeoutSec: number;
  /** When false the hook never transitions to idle. */
  enabled: boolean;
  onIdle?: () => void;
  onActive?: () => void;
}

interface UseIdleReturn {
  isIdle: boolean;
  /** External wake — call from Homey-sensor / camera motion handlers. */
  poke: () => void;
}

const ACTIVITY_EVENTS = [
  "pointerdown",
  "mousemove",
  "keydown",
  "touchstart",
  "visibilitychange",
] as const;

/**
 * Tracks user idle state via pointer, mouse, keyboard, touch, and visibility
 * events on `window`. Fires `onIdle` once after `timeoutSec` seconds of
 * inactivity, and `onActive` on the first activity that follows idle.
 *
 * SSR-safe (window guard in useEffect). Fully cleans up on unmount.
 * When `enabled` is false the hook stays inactive; no idle is ever fired.
 *
 * `poke()` can be called externally (e.g. from a Homey motion sensor or
 * camera motion handler) to reset the idle timer and, if currently idle,
 * transition back to active.
 */
export function useIdle({ timeoutSec, enabled, onIdle, onActive }: UseIdleOptions): UseIdleReturn {
  const [isIdle, setIsIdle] = useState(false);

  // Hold callbacks in refs so timer closures always call the latest version.
  const onIdleRef = useRef(onIdle);
  const onActiveRef = useRef(onActive);
  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);
  useEffect(() => {
    onActiveRef.current = onActive;
  }, [onActive]);

  // Mutable idle state ref mirrors the state so event handlers can read it
  // synchronously without stale closure issues.
  const isIdleRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleIdle = useCallback(
    (sec: number) => {
      clearTimer();
      timerRef.current = setTimeout(() => {
        isIdleRef.current = true;
        setIsIdle(true);
        onIdleRef.current?.();
      }, sec * 1000);
    },
    [clearTimer],
  );

  const poke = useCallback(() => {
    if (!enabled) return;
    if (isIdleRef.current) {
      // Was idle — transition back to active.
      isIdleRef.current = false;
      setIsIdle(false);
      onActiveRef.current?.();
    }
    scheduleIdle(timeoutSec);
  }, [enabled, timeoutSec, scheduleIdle]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!enabled) {
      clearTimer();
      if (isIdleRef.current) {
        isIdleRef.current = false;
        setIsIdle(false);
      }
      return;
    }

    // Kick off the initial idle timer.
    scheduleIdle(timeoutSec);

    function handleActivity() {
      if (isIdleRef.current) {
        isIdleRef.current = false;
        setIsIdle(false);
        onActiveRef.current?.();
      }
      scheduleIdle(timeoutSec);
    }

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, handleActivity, { passive: true });
    }

    return () => {
      clearTimer();
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, handleActivity);
      }
    };
    // scheduleIdle and clearTimer are stable (useCallback with no deps that change).
    // timeoutSec is listed so the timer is rescheduled when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, timeoutSec]);

  return { isIdle, poke };
}
